export interface Testimonial {
  name: string;
  city: string;
  quote: string;
}

export interface Tomo {
  n: number;
  color: string;
  title: string;
  summary: string;
  detail?: string;
  checks?: string[];
}

export interface Bono {
  n: number;
  title: string;
  desc: string;
  price: string;
  mockup: string;
}

const BONO_IMAGE_BASE =
  'https://aekpgsbqjzqntlbucbts.supabase.co/storage/v1/object/public/store-resources/33ae062c-da1e-4afc-8c72-88d11610dcf3/';

export interface PricingOption {
  id: 'crochet-pro';
  name: string;
  description: string;
  price: string;
  checkoutUrl: string;
}

export const HERO_IMAGE =
  'https://aekpgsbqjzqntlbucbts.supabase.co/storage/v1/object/public/store-resources/33ae062c-da1e-4afc-8c72-88d11610dcf3/1790372437515_hero-11zon.webp';

export const HERO_FEATURES: string[] = [
  '100% digital',
  'Acceso inmediato',
  'Desde nivel inicial',
  'Modelos para crear y vender',
];

export const FULL_TESTIMONIALS: Testimonial[] = [
  {
    name: 'Mariela F.',
    city: 'Rosario',
    quote:
      'Yo sabía algunos puntos básicos, pero nunca lograba terminar un bolso que quedara realmente prolijo. Con el material pude seguir todo mucho más ordenado y ya hice mis primeros modelos.',
  },
  {
    name: 'Laura G.',
    city: 'Córdoba',
    quote:
      'Lo que más me gustó fue tener los modelos organizados. Antes guardaba videos, capturas y patrones por todos lados. Ahora tengo una guía clara para seguir.',
  },
  {
    name: 'Verónica M.',
    city: 'Mendoza',
    quote:
      'Quería empezar a vender algo hecho por mí para el verano y no sabía qué modelos hacer. Los bolsos me dieron una idea mucho más clara de cómo armar una colección.',
  },
  {
    name: 'Sofía P.',
    city: 'Buenos Aires',
    quote:
      'Me sirvió muchísimo la parte de costos y terminaciones. Antes hacía cosas lindas, pero no sabía cuánto cobrar ni cómo presentarlas.',
  },
];

export const COST_STATS: { value: string; unit: string; title: string; desc: string }[] = [
  {
    value: '20+',
    unit: 'pestañas abiertas',
    title: 'Buscando patrones distintos',
    desc: 'Pinterest, YouTube, Instagram, capturas y tutoriales guardados que después no encontrás.',
  },
  {
    value: 'Horas',
    unit: 'probando y destejiendo',
    title: 'Sin una estructura clara',
    desc: 'Un pequeño error puede obligarte a volver atrás una y otra vez.',
  },
  {
    value: '1',
    unit: 'colección',
    title: 'Que nunca terminás de lanzar',
    desc: 'Tenés ganas de vender tus tejidos, pero siempre falta definir modelos, materiales, precios o terminaciones.',
  },
];

export const PAIN_CARDS: { title: string; desc: string }[] = [
  {
    title: 'Empezás un bolso y no sabés cómo seguir',
    desc: 'Encontrás una foto hermosa, pero no tenés medidas, cantidades ni instrucciones claras.',
  },
  {
    title: 'Guardás cientos de ideas',
    desc: 'Tenés Pinterest, Instagram y el celular llenos de bolsos que algún día querés hacer. Pero terminás sin saber por cuál empezar.',
  },
  {
    title: 'Comprás hilo de más o de menos',
    desc: 'No sabés cuánto material necesita cada proyecto y terminás gastando de más o quedándote corta.',
  },
  {
    title: 'Los bolsos pierden forma',
    desc: 'El modelo se veía increíble en la foto, pero al terminarlo queda flojo, torcido o sin estructura.',
  },
  {
    title: 'No sabés cómo ponerles precio',
    desc: 'Sumás el hilo, pero olvidás accesorios, horas de trabajo, packaging y margen.',
  },
  {
    title: 'Querés vender, pero no sabés qué ofrecer',
    desc: 'Sabés tejer. El problema es convertir esa habilidad en una colección atractiva que la gente realmente quiera comprar.',
  },
];

export const RESTART_STEPS: string[] = [
  'Elegir punto',
  'Buscar medidas',
  'Definir el hilo',
  'Calcular cantidades',
  'Resolver asas',
  'Pensar terminaciones',
];

export const JOURNEY: { label: string; quote: string }[] = [
  { label: 'De', quote: 'Me gustaría hacer ese bolso' },
  { label: 'A', quote: 'Sé exactamente cómo hacerlo.' },
  { label: 'Y después', quote: 'Ahora también puedo venderlo.' },
];

export const TOMOS: Tomo[] = [
  {
    n: 1,
    color: '#A9502F',
    title: 'Crochet desde cero',
    summary: 'Materiales, agujas, tensión, puntos, aumentos, disminuciones y bases.',
    detail: 'Construí una base sólida antes de empezar tus primeros modelos.',
  },
  {
    n: 2,
    color: '#8C6440',
    title: 'Bolsos esenciales',
    summary: 'Aprendé a crear modelos prácticos y versátiles.',
    detail: 'Tote bags, shoppers, bolsos calados y modelos ideales para comenzar.',
  },
  {
    n: 3,
    color: '#5F6A43',
    title: 'Bolsos tendencia verano',
    summary: 'Diseños pensados para la temporada.',
    detail: 'Mini bags, hobo bags, bolsos de playa, media luna y estilos modernos.',
  },
  {
    n: 4,
    color: '#86391F',
    title: 'Bolsos premium',
    summary: 'Llevá tus terminaciones a otro nivel.',
    detail: 'Rafia, cordón, asas especiales, cierres, forros y estructuras.',
  },
  {
    n: 5,
    color: '#7A5A45',
    title: 'Modelos paso a paso',
    summary: 'Una colección de diseños completos con:',
    checks: ['materiales', 'medidas', 'puntos', 'construcción', 'armado', 'terminaciones'],
  },
  {
    n: 6,
    color: '#3A2920',
    title: 'Crochet para vender',
    summary: 'Transformá tu habilidad en un producto. Aprendé sobre:',
    checks: ['costos', 'precios', 'presentación', 'fotografías', 'packaging', 'catálogo', 'ventas'],
  },
];

export const BONOS: Bono[] = [
  {
    n: 1,
    title: 'Paletas de colores para bolsos',
    mockup: `${BONO_IMAGE_BASE}1790372442125_bono1-5-11zon.webp`,
    desc: 'Combinaciones listas para usar en colecciones modernas y de verano.',
    price: '$8.990',
  },
  {
    n: 2,
    title: 'Guía de materiales y accesorios',
    mockup: `${BONO_IMAGE_BASE}1790372443217_bono2-4-11zon.webp`,
    desc: 'Hilos, asas, cierres, argollas, forros y complementos.',
    price: '$9.990',
  },
  {
    n: 3,
    title: 'Calculadora de costos y precios',
    mockup: `${BONO_IMAGE_BASE}1790372444346_bono3-3-11zon.webp`,
    desc: 'Organizá materiales, tiempo de trabajo y margen antes de vender.',
    price: '$12.990',
  },
  {
    n: 4,
    title: 'Catálogo de inspiración',
    mockup: `${BONO_IMAGE_BASE}1790372445507_bono4-2-11zon.webp`,
    desc: 'Ideas de modelos, estilos y combinaciones para crear nuevas colecciones.',
    price: '$8.990',
  },
  {
    n: 5,
    title: 'Crochet Emprende',
    mockup: `${BONO_IMAGE_BASE}1790372446652_bono5-1-11zon.webp`,
    desc: 'Ideas para presentar, fotografiar y promocionar tus bolsos en redes sociales.',
    price: '$8.990',
  },
];

export const BEFORE_AFTER = {
  before: [
    'Patrones guardados por todos lados',
    'Proyectos incompletos',
    'Dudas con materiales',
    'Modelos sin estructura',
    'Precios improvisados',
    'Horas perdidas buscando ideas',
  ],
  after: [
    'Modelos organizados',
    'Instrucciones claras',
    'Materiales definidos',
    'Mejores terminaciones',
    'Costos más ordenados',
    'Una colección que podés mostrar y vender',
  ],
};

export const NOT_NEEDED: string[] = [
  'Ser experta',
  'Tener experiencia vendiendo',
  'Conocer puntos avanzados',
  'Comprar máquinas',
  'Tener un local',
];

export const ONLY_NEEDED: string[] = ['Hilo', 'Una aguja', 'Tu celular o computadora', 'Ganas de crear'];

export const SUMMER_BLEND: string[] = ['artesanía', 'moda', 'funcionalidad', 'diseño'];

export const SUMMER_VARIANTS: string[] = ['colores', 'materiales', 'tamaños', 'asas', 'detalles', 'terminaciones'];

export const STRUCTURE_STEPS: string[] = [
  'Vos elegís el modelo.',
  'Preparás tus materiales.',
  'Y empezás a tejer.',
];

export const FOR_WHO: string[] = [
  'Querés aprender crochet desde cero',
  'Ya tejés pero querés mejorar tus bolsos',
  'Querés hacer modelos modernos',
  'Buscás ideas para primavera/verano',
  'Querés empezar un emprendimiento',
  'Ya vendés tejidos y querés sumar productos',
  'Querés dejar de depender de tutoriales sueltos',
];

export const FOR_WHO_THOUGHTS: string[] = [
  'Me encantaría vender lo que tejo.',
  'Quiero hacer bolsos pero no sé por dónde empezar.',
  'Siempre veo modelos hermosos y no sé cómo hacerlos.',
  'No sé cuánto cobrar.',
  'Quiero aprovechar el verano para vender.',
  'Necesito algo más ordenado que YouTube y Pinterest.',
];

export const NEXT_STEPS: string[] = [
  'Empezá por uno.',
  'Después por otro.',
  'Aprendé el proceso.',
  'Mejorá tus terminaciones.',
  'Sacá tus primeras fotos.',
  'Calculá tu precio.',
  'Y construí desde ahí.',
];

export const VALUE_STACK: { label: string; detail: string; value: string; strike?: boolean }[] = [
  { label: 'Megapack Bolsos Crochet Pro', detail: '6 tomos completos', value: '$48.990' },
  { label: 'Bono 1 · Paletas de colores', detail: 'Combinaciones para colecciones', value: '$8.990', strike: true },
  { label: 'Bono 2 · Materiales y accesorios', detail: 'Hilos, asas, cierres y forros', value: '$9.990', strike: true },
  { label: 'Bono 3 · Calculadora de costos', detail: 'Materiales, tiempo y margen', value: '$12.990', strike: true },
  { label: 'Bono 4 · Catálogo de inspiración', detail: 'Ideas para nuevas colecciones', value: '$8.990', strike: true },
  { label: 'Bono 5 · Crochet Emprende', detail: 'Presentar y promocionar', value: '$8.990', strike: true },
];

export const FAQS: { q: string; a: string }[] = [
  {
    q: '¿Necesito saber crochet?',
    a: 'No. El primer tomo comienza con materiales, agujas y puntos fundamentales.',
  },
  {
    q: '¿Es un producto físico?',
    a: 'No. El Megapack Bolsos Crochet Pro es 100% digital.',
  },
  {
    q: '¿Puedo verlo desde el celular?',
    a: 'Sí. Podés consultar los materiales desde celular, tablet o computadora.',
  },
  {
    q: '¿Sirve si ya sé tejer?',
    a: 'Sí. También incluye modelos, estructuras, terminaciones y contenido orientado a quienes quieren mejorar o vender.',
  },
  {
    q: '¿Puedo vender los bolsos que haga?',
    a: 'El material está pensado para enseñarte a crear productos físicos de crochet que luego puedas incorporar a tu propio emprendimiento.',
  },
  {
    q: '¿Incluye modelos de verano?',
    a: 'Sí. Una parte importante del contenido está orientada a bolsos, estilos y materiales ideales para primavera/verano.',
  },
];

export const PRICING_OPTIONS: PricingOption[] = [
  {
    id: 'crochet-pro',
    name: 'Megapack Bolsos Crochet Pro',
    description: '6 Tomos + 5 Bonos',
    price: '$19.990',
    checkoutUrl: 'https://megapack-crochet-bolsos-pro.impultienda.ar/checkout',
  },
];

export const PRICING = {
  currency: 'ARS',
  bonusValueOld: '$49.950',
  totalValueOld: '$98.940',
  savings: '$78.950',
};
